// Angular
// Models
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import addDays from 'date-fns/add_days';
import { omit as _omit } from 'lodash';

import { env } from '@amfam/environment';
import {
  FromAddresse,
  GetReplaceVehicleResponse,
  INotify,
  MessageDetail,
  QuickQuoteApiPayload,
  QuickQuoteRequest,
  ReplaceVehiclePLPCPayload,
  SubmitOnlineBindRequestModel,
  TemplatedEmail,
  TransactionResponse
} from '@amfam/policy/models';
// Misc
import { PrettyPolicyNum } from '@amfam/policy/pipes';
import { ApiStatus } from '@amfam/shared/models';
// Services
import {
  Addressee,
  ANSNotification,
  InternalEmail,
  UtilService
} from '@amfam/shared/utility/shared-services';

import * as quickQuoteDataActions from '../+state/quick-quote-data/quick-quote-data.actions';

@Injectable({ providedIn: 'root' })
export class QuickQuoteDataService {
  constructor(
    private http: HttpClient,
    private utilService: UtilService
  ) {}

  getQuickQuote(
    policyNumber: string,
    apiPayload: QuickQuoteApiPayload,
    operation: QuickQuoteRequest['operation']
  ) {
    if (operation === 'quickquotes') {
      const { year, make, model, series, vehicleType, ...rest } = apiPayload.newVehicle;
      apiPayload = {
        ...apiPayload,
        newVehicle: { year, make, model, series: series || '', vehicleType }
      };
    }
    const uri = `${env.autoWhatIfApi}autopolicies/${policyNumber}/${operation}`;
    return this.http.post<GetReplaceVehicleResponse>(uri, apiPayload);
  }

  getQuotePLPC(payload: ReplaceVehiclePLPCPayload) {
    const uri = env.plpcContextAPI + 'replace-vehicle';
    let headers = new HttpHeaders();
    if (env.applicationTitle === 'MyAccount - INT - 01') {
      headers = headers.set('afi-leg', '01');
    }
    return this.http.post<TransactionResponse>(uri, payload, {
      headers
    });
  }

  mapCustomerEmailSuccessAction(type: string) {
    let action;
    switch (type) {
      case quickQuoteDataActions.quickQuoteSendCustomerEmail.type:
        action = quickQuoteDataActions.quickQuoteSendCustomerEmailSuccess();
        break;

      case quickQuoteDataActions.quickQuoteSendToAgentCustomerEmail.type:
        action = quickQuoteDataActions.quickQuoteSendToAgentCustomerEmailSuccess();
        break;

      case quickQuoteDataActions.quickQuoteEmailCustomerOnError.type:
        action = quickQuoteDataActions.quickQuoteEmailCustomerOnErrorSuccess();
        break;

      case quickQuoteDataActions.emailCustomerOnBind.type:
        action = quickQuoteDataActions.emailCustomerOnBindSuccess();
        break;
    }
    return action;
  }

  mapCustomerEmailFailureAction(type: string, error: { status: ApiStatus }) {
    let action;
    switch (type) {
      case quickQuoteDataActions.quickQuoteSendCustomerEmail.type:
        action = quickQuoteDataActions.quickQuoteSendCustomerEmailFailed({ status: error.status });
        break;

      case quickQuoteDataActions.quickQuoteSendToAgentCustomerEmail.type:
        action = quickQuoteDataActions.quickQuoteSendToAgentCustomerEmailFailed({
          status: error.status
        });
        break;

      case quickQuoteDataActions.quickQuoteEmailCustomerOnError.type:
        action = quickQuoteDataActions.quickQuoteEmailCustomerOnErrorFailed({
          status: error.status
        });
        break;

      case quickQuoteDataActions.emailCustomerOnBind.type:
        action = quickQuoteDataActions.emailCustomerOnBindFailed({
          status: error.status
        });
        break;
    }
    return action;
  }

  mapAgentEmailSuccessAction(type: string) {
    let action;
    switch (type) {
      case quickQuoteDataActions.quickQuoteSendToAgentEmail.type:
        action = quickQuoteDataActions.quickQuoteSendToAgentEmailSuccess();
        break;

      case quickQuoteDataActions.quickQuoteEmailAgentOnError.type:
        action = quickQuoteDataActions.quickQuoteEmailAgentOnErrorSuccess();
        break;

      case quickQuoteDataActions.emailAgentOnBind.type:
        action = quickQuoteDataActions.emailAgentOnBindSuccess();
        break;
    }
    return action;
  }

  mapAgentEmailFailureAction(type: string, status: ApiStatus) {
    let action;
    switch (type) {
      case quickQuoteDataActions.quickQuoteSendToAgentEmail.type:
        action = quickQuoteDataActions.quickQuoteSendToAgentEmailFailed({ status });
        break;

      case quickQuoteDataActions.quickQuoteEmailAgentOnError.type:
        action = quickQuoteDataActions.quickQuoteEmailAgentOnErrorFailed({ status });
        break;

      case quickQuoteDataActions.emailAgentOnBind.type:
        action = quickQuoteDataActions.emailAgentOnBindFailed();
        break;
    }
    return action;
  }

  mapAnsSuccessAction(type: string) {
    let action;
    switch (type) {
      case quickQuoteDataActions.ansNotification.type:
        action = quickQuoteDataActions.ansNotificationSuccess();
        break;
      case quickQuoteDataActions.ansNotificationOnBind.type:
        action = quickQuoteDataActions.ansNotificationOnBindSuccess();
        break;
      default:
        action = quickQuoteDataActions.ansNotificationOnErrorSuccess();
        break;
    }
    return action;
  }

  mapAnsFailureAction(type: string, status: ApiStatus) {
    let action;
    switch (type) {
      case quickQuoteDataActions.ansNotification.type:
        action = quickQuoteDataActions.ansNotificationFailed({ status });
        break;
      case quickQuoteDataActions.ansNotificationOnBind.type:
        action = quickQuoteDataActions.ansNotificationOnBindFailed({ status });
        break;
      default:
        action = quickQuoteDataActions.ansNotificationOnErrorFailed({ status });
        break;
    }
    return action;
  }

  mapQuickQuoteRequest(request: QuickQuoteRequest) {
    const { policyNumber, ...apiPayload } = request;
    return { policyNumber, apiPayload };
  }

  mapPremiumEstimate(
    plpcReplaceVehiclePayload: TransactionResponse
  ): TransactionResponse & GetReplaceVehicleResponse {
    return {
      ...plpcReplaceVehiclePayload,
      premium: {
        monthlyPremium: plpcReplaceVehiclePayload?.result?.rate?.monthlyPremium as any as number,
        termMonths: plpcReplaceVehiclePayload?.result?.rate?.termMonths,
        totalDiscount: plpcReplaceVehiclePayload?.result?.rate?.totalDiscountAmount,
        totalPremium: plpcReplaceVehiclePayload?.result?.rate?.totalCost,
        workOrderNumber: plpcReplaceVehiclePayload?.result?.submissionId
      }
    };
  }

  public submitOnlineBind(model: SubmitOnlineBindRequestModel) {
    const uri = `${env.autoWhatIfApi}autoquotes/${model.workOrderNumber}/submit`;
    return this.http.post<ApiStatus>(uri, _omit(model, ['workOrderNumber', 'vehicleId']));
  }

  public submitOnlineBindPLPC(model: SubmitOnlineBindRequestModel, vin: string) {
    const uri = env.plpcContextAPI + `bind/${model.policyNumber}/${vin}`;
    let headers = new HttpHeaders();
    if (env.applicationTitle === 'MyAccount - INT - 01') {
      headers = headers.set('afi-leg', '01');
    }
    return this.http.post<ApiStatus>(
      uri,
      {},
      {
        headers
      }
    );
  }

  public prepareNotify(model: SubmitOnlineBindRequestModel): INotify {
    // ANS
    const ans: ANSNotification = {
      notificationsRequest: {
        notificationText: model.notificationText,
        notificationType: model.notificationType,
        customerName: `${model.customer.firstName} ${model.customer.lastName}`,
        referenceNumber: model.policyNumber,
        policyNumber: model.policyNumber,
        createdBy: 'DMSI',
        formattedReferenceNumber:
          new PrettyPolicyNum().transform(model.policyNumber) || model.policyNumber,
        from: 'DMSI',
        dueDate: addDays(new Date(), 2).toISOString(),
        sourceSystem: 'MYACCOUNT',
        uniqueId: this.utilService.generateId()
      },
      producerId: model.producer?.producerId
    };

    // EMAIL
    const email: InternalEmail = new InternalEmail();
    email.messageFrom = new Addressee(FromAddresse.ADDRESS, FromAddresse.NAME);
    email.messageToList = [
      new Addressee(
        model.producer.email,
        `${model.producer?.firstName || ''}  ${model.producer?.lastName || ''}`
      )
    ];
    email.messageSubject = model.emailSubject;
    email.messageContent = model.emailBody;

    // CUSTOMER EMAIL
    const customerEmail: TemplatedEmail = this.buildCustomerEmail(model);
    return { ans, email, customerEmail };
  }

  private buildCustomerEmail(model: SubmitOnlineBindRequestModel): TemplatedEmail {
    const messageDetails: MessageDetail[] = [];
    messageDetails.push(new MessageDetail('EmailAddress', model.customer.email || ''));
    messageDetails.push(new MessageDetail('SubscriberKey', model.customer.email || ''));
    messageDetails.push(new MessageDetail('FirstName', model.customer.firstName || ''));
    messageDetails.push(new MessageDetail('ProdID', model.producer.producerId || ''));
    messageDetails.push(new MessageDetail('PolicyNumber', model.policyNumber || ''));
    messageDetails.push(
      new MessageDetail('NewRiskInformation', model.emailTemplateData.NewRiskInformation || '')
    );
    messageDetails.push(
      new MessageDetail(
        'NewTotalPremium',
        this.formatAmount(model.emailTemplateData.NewTotalPremium)
      )
    );
    messageDetails.push(
      new MessageDetail(
        'NewMonthlyPremium',
        this.formatAmount(model.emailTemplateData.NewMonthlyPremium)
      )
    );
    messageDetails.push(
      new MessageDetail('NewTermDescription', model.emailTemplateData?.NewTermDescription || '')
    );
    messageDetails.push(
      new MessageDetail('RiskInformation', model.emailTemplateData?.RiskInformation || '')
    );
    messageDetails.push(
      new MessageDetail('TotalPremium', this.formatAmount(model.emailTemplateData.TotalPremium))
    );
    messageDetails.push(
      new MessageDetail('MonthlyPremium', this.formatAmount(model.emailTemplateData.MonthlyPremium))
    );
    messageDetails.push(
      new MessageDetail('TermDescription', model.emailTemplateData.TermDescription || '')
    );
    messageDetails.push(new MessageDetail('VehicleCount', model.emailTemplateData.VehicleCount));
    messageDetails.push(...this.buildMessageFromMap(model.vehicleCoverages));
    messageDetails.push(...this.buildMessageFromMap(model.policyCoverages));
    messageDetails.push(...this.buildMessageFromMap(model.discounts));
    messageDetails.push(...this.buildMessageFromMap(model.futurePayments));

    if (model.futurePayments && model.futurePayments.size > 0)
      messageDetails.push(
        new MessageDetail('PaymentFrequency', model.futurePayments.size > 2 ? 'Monthly' : 'FullPay')
      );
    const email = new TemplatedEmail(
      model.customer.emailTemplateId,
      model.partnerId,
      'MyAccount',
      'EN',
      model.customer.id,
      model.customer.email,
      messageDetails
    );
    return email;
  }

  formatAmount(value: number): string | null {
    const dollarUS = Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    });
    if (value) return dollarUS.format(value);
    return null;
  }

  private buildMessageFromMap(data: Map<string, string> | undefined) {
    const messageDetails: MessageDetail[] = [];
    if (data && data.size > 0)
      data.forEach(function (value, key) {
        messageDetails.push(new MessageDetail(key, value));
      });
    return messageDetails;
  }
}
